/**
 * @homeaway/raf-optimized
 * Inspired by https://developer.mozilla.org/en-US/docs/Web/Events/resize
 *
 **/


let _clientWidth = -1;
let _clientHeight = -1;
let _scrollTop = -1;
let _scrollLeft = -1;
let _mouseX = -1;
let _mouseY = -1;
let _resizeQueue = [];
let _scrollQueue = [];
let _mouseQueue = [];
let _init = false;

/**
 * addThrottleHandler()
 * add request animation frame throttled event handler function
 * @param {*} type
 * @param {*} callback
 * @param {*} obj
 */
export const addThrottleHandler = function(type, callback, obj) {
    if (typeof obj !== 'undefined' || typeof window !== 'undefined') {
        obj = obj || window;
        if (!obj.addEventListener) {
            return false;
        }
        let running = false;
        const func = function(originalEvent) {
            if (running) {
                return;
            }
            running = true;
            requestAnimationFrame(function() {
                callback(originalEvent);
                running = false;
            });
        };
        obj.addEventListener(type, func);
        return function() {
            obj.removeEventListener(type, func);
        };
    }
    return false;
};

function init() {
    if (_init === true) {
        return true;
    }
    if (typeof window === 'undefined') {
        return false;
    }

    let supportWindowScrollObj = window.scrollY !== undefined;

    function getClientWidth() {
        return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    }

    function getClientHeight() {
        return window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    }

    function getClientScrollTop() {
        return supportWindowScrollObj ? window.scrollY : document.documentElement.scrollTop || document.body.scrollTop;
    }

    function getClientScrollLeft() {
        return supportWindowScrollObj ? window.scrollX : document.documentElement.scrollLeft || document.body.scrollLeft;
    }

    _clientWidth = getClientWidth();
    _clientHeight = getClientHeight();
    _scrollTop = getClientScrollTop();
    _scrollLeft = getClientScrollLeft();

    function handleResize() {
        if (_resizeQueue.length > 0) {
            let clientWidth = getClientWidth();
            let clientHeight = getClientHeight();
            let didResize = !!(_clientWidth !== clientWidth || _clientHeight !== clientHeight);

            if (didResize) {
                for (let i = 0; i < _resizeQueue.length; i++) {
                    _resizeQueue[i]({
                        width: clientWidth,
                        height: clientHeight
                    });
                }
            }
            _clientWidth = clientWidth;
            _clientHeight = clientHeight;
        }
    }

    function handleScroll() {
        if (_scrollQueue.length > 0) {
            let scrollTop = getClientScrollTop();
            let scrollLeft = getClientScrollLeft();
            let didVerticalScroll = scrollTop !== _scrollTop;
            let didHorizontalScroll = scrollLeft !== _scrollLeft;
            let direction = (didVerticalScroll ? (scrollTop > _scrollTop ? 'down' : 'up') : null) ||
                    (didHorizontalScroll ? (scrollLeft > _scrollLeft ? 'right' : 'left') : null);

            if (didVerticalScroll || didHorizontalScroll) {
                for (let i = 0; i < _scrollQueue.length; i++) {
                    _scrollQueue[i]({scrollTop, scrollLeft, direction});
                }
            }
            _scrollTop = scrollTop;
            _scrollLeft = scrollLeft;
        }
    }

    function handleMouseMove(event) {
        if (_mouseQueue.length > 0) {
            var mouseX = event.x;
            var mouseY = event.y;

            let didVerticalMouse = mouseY !== _mouseY;
            let didHorizontalMouse = mouseX !== _mouseX;
            let distance = Math.abs(mouseY - _mouseY) + Math.abs(mouseX - _mouseX);

            let direction = `${(didVerticalMouse ? (mouseY > _mouseY ? 'down' : 'up') : 'none')}:${(didHorizontalMouse ? (mouseX > _mouseX ? 'right' : 'left') : 'none')}`;

            if (didVerticalMouse || didHorizontalMouse) {
                for (let i = 0; i < _mouseQueue.length; i++) {
                    _mouseQueue[i]({mouseX, mouseY, direction, distance});
                }
            }
            _mouseX = mouseX;
            _mouseY = mouseY;
        }
    }

    /* init - you can init any event */
    addThrottleHandler('resize', handleResize);
    addThrottleHandler('scroll', handleScroll);
    addThrottleHandler('mousemove', handleMouseMove);

    /* Do not re-initialize this */
    _init = true;

    return true;
}

/**
 * resize()
 * {function} cb - function to be called when rAF optimized resize event occurs
 * TODO: support unregister
 */
export function resize(cb) {
    if(typeof cb !== 'function' || init() === false) {
        return false;
    }
    _resizeQueue.push(cb);

    return function removeCallback() {
        let index = _resizeQueue.indexOf(cb);

        if (index >= 0) {
            _resizeQueue.splice(index, 1);
            return true;
        }
        return false;
    }
}

/**
 * clearResize()
 * Clears resize queue
 */
export function clearResize() {
    _resizeQueue = [];
}

/**
 * scroll()
 * {function} cb - function to be called when rAF optimized scroll event occurs
 * TODO: support unregister
 */
export function scroll(cb) {
    if(typeof cb !== 'function' || init() === false) {
        return false;
    }
    _scrollQueue.push(cb);

    return function removeCallback() {
        let index = _scrollQueue.indexOf(cb);

        if (index >= 0) {
            _scrollQueue.splice(index, 1);
            return true;
        }
        return false;
    }
}

/**
 * clearScroll()
 * Clears the scroll queue
 */
export function clearScroll() {
    _scrollQueue = [];
}

/**
 * mousemove()
 * {function} cb - function to be called when mousemove occurs
 */
export function mousemove(cb) {
    if(typeof cb !== 'function' || init() === false) {
        return false;
    }
    _mouseQueue.push(cb);

    return function removeCallback() {
        let index = _mouseQueue.indexOf(cb);

        if (index >= 0) {
            _mouseQueue.splice(index, 1);
            return true;
        }
        return false;
    }
}

/**
 * clearScroll()
 * Clears the scroll queue
 */
export function clearMousemove() {
    _mouseQueue = [];
}

/**
 * throttle()
 * Simple throttle function
 * @param {*} fn
 * @param {*} wait
 */
export function throttle(fn, wait) {
    let time = Date.now();
    return function() {
        if ((time + wait - Date.now()) < 0) {
            fn();
            time = Date.now();
        }
    };
}

// Export API to window object
if (typeof window !== 'undefined' && !window.rafoptimized) {
    window.rafoptimized = {
        scroll,
        resize,
        mousemove,
        addThrottleHandler
    };
}
